import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject, takeUntil, timer } from 'rxjs';
import { StorageService } from 'src/app/modules/shared/services/storage.service';
import { BlockUIService } from 'src/app/modules/shared/services/blockUI.service';
import { resolveCompanyId } from 'src/app/utils/tenant-context.util';
import { CorpacerosUserSyncService } from '../../../epps/services/corpaceros-user-sync.service';
import { CorpacerosIntegrationConfigService } from '../../../epps/services/corpaceros-integration-config.service';
import {
  CorpacerosUserSyncCompletedEvent,
  CorpacerosUserSyncFailedEvent,
  CorpacerosUserSyncProgressEvent,
  CorpacerosUserSyncStartedEvent,
  UserSyncLogDTO,
  UserSyncLogsRequest,
  UserSyncLogStatus,
  UserSyncStatus,
} from '../../../epps/models/corpaceros-user-sync.models';
import {
  CorpacerosIntegrationConfigDTO,
  CorpacerosPlaceholder,
  SaveCorpacerosIntegrationConfigCommand,
  TestCorpacerosConnectionCommand,
} from '../../../epps/models/corpaceros-integration-config.models';

@Component({
  selector: 'app-user-sync-settings',
  templateUrl: './user-sync-settings.component.html',
  providers: [ConfirmationService],
})
export class UserSyncSettingsComponent implements OnInit, OnDestroy {
  private syncService = inject(CorpacerosUserSyncService);
  private configService = inject(CorpacerosIntegrationConfigService);
  private storage = inject(StorageService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private blockUI = inject(BlockUIService);
  private fb = inject(FormBuilder);

  companyId: number | null = null;
  status: UserSyncStatus = 'inactivo';

  // --- Estado configuración de conexión ---
  configForm!: FormGroup;
  configId: number | null = null;
  config: CorpacerosIntegrationConfigDTO | null = null;
  showConfigForm = false;
  configMode: 'add' | 'edit' = 'add';
  hasPasswordConfigured = false;
  configLoading = false;
  testingConnection = false;
  availablePlaceholders: CorpacerosPlaceholder[] = [];
  placeholdersLoading = false;
  readonly templatePlaceholder =
    '{\n  "Identificacion_Empleado": "{{identificacion_empleado}}",\n  "Tipo_Ejecucion": "{{tipo_ejecucion}}"\n}';
  readonly timeZoneOptions = [
    { label: 'America/Bogota (Colombia)', value: 'America/Bogota' },
    { label: 'UTC', value: 'UTC' },
    { label: 'America/Mexico_City', value: 'America/Mexico_City' },
    { label: 'America/Lima', value: 'America/Lima' },
  ];
  readonly defaultCronHint = '0 0 23 * * *';

  syncId: string | null = null;
  totalBlocks = 0;
  currentBlock = 0;
  itemsProcessed = 0;
  totalItems = 0;
  progress = 0;

  totalEmployeesReceived = 0;
  usersCreated = 0;
  usersUpdated = 0;
  errors = 0;
  errorMessages: string[] = [];

  errorMessage = '';

  // --- Estado historial ---
  logs: UserSyncLogDTO[] = [];
  logsLoading = false;
  totalLogs = 0;
  logPage = 1;
  logPageSize = 20;
  logFirst = 0;
  filterForm!: FormGroup;

  statusOptions: { label: string; value: UserSyncLogStatus | null }[] = [
    { label: 'Todos', value: null },
    { label: 'Pendiente', value: 'PENDING' },
    { label: 'En progreso', value: 'IN_PROGRESS' },
    { label: 'Completado', value: 'COMPLETED' },
    { label: 'Fallido', value: 'FAILED' },
  ];

  private readonly TIMEOUT_MS = 60000;
  private timeoutTimer$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    const currentCompany = this.storage.getItem('currentCompany', false) as any;
    this.companyId = resolveCompanyId(currentCompany);
    this.buildConfigForm();
    this.loadConfig();
    this.loadPlaceholders();
    this.buildFilterForm();
    this.subscribeToSyncEvents();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.timeoutTimer$.next();
    this.timeoutTimer$.complete();
  }

  confirmStart(event: Event): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: '¿Desea iniciar la sincronización de usuarios con Corpaceros?',
      icon: 'pi pi-sync',
      acceptLabel: 'Sí, sincronizar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-info p-button-sm',
      accept: () => this.startSync(),
    });
  }

  startSync(): void {
    if (!this.companyId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se ha seleccionado una compañía válida',
      });
      return;
    }

    this.resetState();
    this.status = 'iniciando';
    this.startTimeoutTimer();

    this.syncService.startSync(this.companyId).subscribe({
      error: () => {
        this.status = 'error';
        this.errorMessage = 'No se pudo iniciar la sincronización. Intente nuevamente.';
      },
    });
  }

  get canStart(): boolean {
    return this.status !== 'iniciando' && this.status !== 'en-progreso';
  }

  private subscribeToSyncEvents(): void {
    this.syncService.syncStarted$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: CorpacerosUserSyncStartedEvent) => {
        this.timeoutTimer$.next();
        this.status = 'en-progreso';
        this.syncId = event.syncId;
      });

    this.syncService.syncProgress$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: CorpacerosUserSyncProgressEvent) => {
        this.currentBlock = event.blockNumber;
        this.totalBlocks = event.totalBlocks;
        this.itemsProcessed = event.itemsProcessed;
        this.totalItems = event.totalItems;
        this.usersCreated = event.usersCreated;
        this.usersUpdated = event.usersUpdated;
        this.errors = event.errors;
        this.progress = this.totalItems > 0 ? Math.round((this.itemsProcessed / this.totalItems) * 100) : 0;
      });

    this.syncService.syncCompleted$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: CorpacerosUserSyncCompletedEvent) => {
        this.status = 'completado';
        this.progress = 100;
        this.totalEmployeesReceived = event.totalEmployeesReceived;
        this.usersCreated = event.usersCreated;
        this.usersUpdated = event.usersUpdated;
        this.errors = event.errors;
        this.errorMessages = event.errorMessages || [];
        this.loadLogs(1);
      });

    this.syncService.syncFailed$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: CorpacerosUserSyncFailedEvent) => {
        this.status = 'error';
        this.errorMessage = event.error;
      });
  }

  private startTimeoutTimer(): void {
    timer(this.TIMEOUT_MS)
      .pipe(takeUntil(this.timeoutTimer$), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.status === 'iniciando') {
          this.status = 'timeout';
          this.errorMessage = 'Tiempo de espera agotado. No se recibió respuesta de Corpaceros.';
        }
      });
  }

  private resetState(): void {
    this.syncId = null;
    this.totalBlocks = 0;
    this.currentBlock = 0;
    this.itemsProcessed = 0;
    this.totalItems = 0;
    this.progress = 0;
    this.totalEmployeesReceived = 0;
    this.usersCreated = 0;
    this.usersUpdated = 0;
    this.errors = 0;
    this.errorMessages = [];
    this.errorMessage = '';
  }

  // ============================================================
  // HISTORIAL
  // ============================================================
  loadLogs(page = 1): void {
    if (!this.companyId) return;
    const f = this.filterForm.getRawValue();
    this.logPage = page;
    this.logsLoading = true;

    const req: UserSyncLogsRequest = {
      companyId: this.companyId,
      pageNumber: page,
      pageSize: this.logPageSize,
      status: f.status ?? null,
      dateFrom: f.dateFrom ? (f.dateFrom as Date).toISOString() : null,
      dateTo: f.dateTo ? (f.dateTo as Date).toISOString() : null,
    };

    this.syncService.getLogs(req).subscribe({
      next: (res) => {
        const data = res.data;
        this.logs = data?.items ?? [];
        this.totalLogs = data?.totalCount ?? 0;
        this.logsLoading = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo cargar el historial',
        });
        this.logsLoading = false;
      },
    });
  }

  onLogPageChange(event: any): void {
    this.logPageSize = event.rows;
    this.logFirst = event.first;
    this.loadLogs(Math.floor(event.first / event.rows) + 1);
  }

  clearLogFilters(): void {
    this.filterForm.reset({ status: null, dateFrom: null, dateTo: null });
    this.loadLogs(1);
  }

  getStatusSeverity(status: UserSyncLogStatus): 'success' | 'secondary' | 'info' | 'warning' | 'danger' {
    const map: Record<UserSyncLogStatus, 'success' | 'secondary' | 'info' | 'warning' | 'danger'> = {
      PENDING: 'warning',
      IN_PROGRESS: 'info',
      COMPLETED: 'success',
      FAILED: 'danger',
    };
    return map[status] ?? 'secondary';
  }

  getStatusLabel(status: UserSyncLogStatus): string {
    const map: Record<UserSyncLogStatus, string> = {
      PENDING: 'Pendiente',
      IN_PROGRESS: 'En progreso',
      COMPLETED: 'Completado',
      FAILED: 'Fallido',
    };
    return map[status] ?? status;
  }

  private buildFilterForm(): void {
    this.filterForm = this.fb.group({
      status: [null],
      dateFrom: [null],
      dateTo: [null],
    });
  }

  // ============================================================
  // CONFIGURACIÓN DE CONEXIÓN (JDE / Corpaceros)
  // ============================================================
  private buildConfigForm(): void {
    this.configForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      isActive: [true],
      isAutoSync: [true],
      cronExpression: ['0 0 23 * * *'],
      timeZoneId: ['America/Bogota'],
      employeesApiUrl: ['', [Validators.required, Validators.pattern('https?://.+')]],
      username: ['', Validators.required],
      password: [''],
      timeoutSeconds: [30, [Validators.required, Validators.min(1)]],
      maxRetries: [3, [Validators.required, Validators.min(0)]],
      payloadTemplate: [''],
    });
  }

  loadPlaceholders(): void {
    this.placeholdersLoading = true;
    this.configService.getAvailablePlaceholders().subscribe({
      next: (data) => {
        this.availablePlaceholders = data ?? [];
        this.placeholdersLoading = false;
      },
      error: () => {
        this.placeholdersLoading = false;
      },
    });
  }

  copyPlaceholder(key: string): void {
    navigator.clipboard.writeText(key).then(() => {
      this.messageService.add({
        severity: 'info',
        summary: 'Copiado',
        detail: `${key} copiado al portapapeles`,
        life: 2000,
      });
    });
  }

  private loadConfig(): void {
    if (!this.companyId) return;
    this.configLoading = true;
    this.configService.getByCompany(this.companyId).subscribe({
      next: (res) => {
        this.config = res.data ?? null;
        this.configId = this.config?.id ?? null;
        this.hasPasswordConfigured = this.config?.hasPasswordConfigured ?? false;
        this.updatePasswordValidator();
        this.configLoading = false;
      },
      error: () => {
        this.configLoading = false;
      },
    });
  }

  private updatePasswordValidator(): void {
    const passwordControl = this.configForm.get('password');
    if (!this.configId) {
      passwordControl?.setValidators([Validators.required]);
    } else {
      passwordControl?.clearValidators();
    }
    passwordControl?.updateValueAndValidity();
  }

  private resetConfigForm(): void {
    this.configForm.reset({
      name: '',
      description: '',
      isActive: true,
      isAutoSync: true,
      cronExpression: '0 0 23 * * *',
      timeZoneId: 'America/Bogota',
      employeesApiUrl: '',
      username: '',
      password: '',
      timeoutSeconds: 30,
      maxRetries: 3,
      payloadTemplate: '',
    });
    this.updatePasswordValidator();
  }

  openNewConfig(): void {
    this.configMode = 'add';
    this.configId = null;
    this.hasPasswordConfigured = false;
    this.resetConfigForm();
    this.showConfigForm = true;
  }

  openEditConfig(): void {
    if (!this.config) return;
    this.configMode = 'edit';
    this.configId = this.config.id;
    this.hasPasswordConfigured = this.config.hasPasswordConfigured;
    this.configForm.patchValue({
      name: this.config.name,
      description: this.config.description,
      isActive: this.config.isActive,
      isAutoSync: this.config.isAutoSync,
      cronExpression: this.config.cronExpression ?? '0 0 23 * * *',
      timeZoneId: this.config.timeZoneId ?? 'America/Bogota',
      employeesApiUrl: this.config.employeesApiUrl,
      username: this.config.username,
      timeoutSeconds: this.config.timeoutSeconds,
      maxRetries: this.config.maxRetries,
      payloadTemplate: this.config.payloadTemplate ?? '',
    });
    this.configForm.get('password')?.reset('');
    this.updatePasswordValidator();
    this.showConfigForm = true;
  }

  closeConfigForm(): void {
    this.showConfigForm = false;
    this.resetConfigForm();
  }

  confirmDeleteConfig(event: Event): void {
    if (!this.configId) return;
    const idToDelete = this.configId;
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: '¿Desea eliminar la configuración de conexión?',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger p-button-sm',
      accept: () => {
        this.blockUI.start();
        this.configService.delete(idToDelete).subscribe({
          next: () => {
            this.config = null;
            this.configId = null;
            this.hasPasswordConfigured = false;
            this.messageService.add({
              severity: 'success',
              summary: 'Eliminado',
              detail: 'Configuración eliminada correctamente',
            });
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo eliminar la configuración',
            });
            this.blockUI.stop();
          },
          complete: () => this.blockUI.stop(),
        });
      },
    });
  }

  saveConfig(): void {
    if (!this.companyId) return;
    if (this.configForm.invalid) {
      this.configForm.markAllAsTouched();
      return;
    }

    const raw = this.configForm.getRawValue();
    const command: SaveCorpacerosIntegrationConfigCommand = {
      id: this.configId,
      companyId: this.companyId,
      name: raw.name,
      description: raw.description || null,
      isActive: raw.isActive,
      isAutoSync: raw.isAutoSync,
      cronExpression: raw.cronExpression?.trim() || null,
      timeZoneId: raw.timeZoneId || 'America/Bogota',
      employeesApiUrl: raw.employeesApiUrl,
      username: raw.username,
      password: raw.password ? raw.password : null,
      timeoutSeconds: raw.timeoutSeconds,
      maxRetries: raw.maxRetries,
      payloadTemplate: raw.payloadTemplate ?? null,
    };

    this.blockUI.start();
    this.configService.save(command).subscribe({
      next: (res) => {
        this.configId = res.data ?? this.configId;
        this.hasPasswordConfigured = this.hasPasswordConfigured || !!raw.password;
        this.config = {
          id: this.configId!,
          companyId: this.companyId!,
          name: raw.name,
          description: raw.description || null,
          isActive: raw.isActive,
          isAutoSync: raw.isAutoSync,
          cronExpression: raw.cronExpression?.trim() || null,
          timeZoneId: raw.timeZoneId || 'America/Bogota',
          employeesApiUrl: raw.employeesApiUrl,
          username: raw.username,
          timeoutSeconds: raw.timeoutSeconds,
          maxRetries: raw.maxRetries,
          hasPasswordConfigured: this.hasPasswordConfigured,
          payloadTemplate: raw.payloadTemplate ?? null,
        };
        this.messageService.add({
          severity: 'success',
          summary: 'Guardado',
          detail: this.configMode === 'add' ? 'Configuración creada correctamente' : 'Configuración actualizada correctamente',
        });
        this.showConfigForm = false;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo guardar la configuración de conexión',
        });
        this.blockUI.stop();
      },
      complete: () => this.blockUI.stop(),
    });
  }

  testConnection(): void {
    const raw = this.configForm.getRawValue();

    if (!raw.employeesApiUrl || !raw.username) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Datos incompletos',
        detail: 'Completa la URL y el usuario antes de probar la conexión',
      });
      return;
    }

    if (!this.configId && !raw.password) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Datos incompletos',
        detail: 'Ingresa la contraseña antes de probar la conexión',
      });
      return;
    }

    const command: TestCorpacerosConnectionCommand = {
      companyId: this.companyId!,
      id: this.configId,
      employeesApiUrl: raw.employeesApiUrl,
      username: raw.username,
      password: raw.password ? raw.password : null,
      timeoutSeconds: raw.timeoutSeconds,
    };

    this.testingConnection = true;
    this.configService.test(command).subscribe({
      next: (res) => {
        const result = res.data;
        this.messageService.add({
          severity: result?.success ? 'success' : 'error',
          summary: result?.success ? 'Conexión exitosa' : 'Conexión fallida',
          detail: result?.message ?? '',
        });
        this.testingConnection = false;
      },
      error: (err) => {
        const status = err?.status ?? 0;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: status
            ? `No se pudo probar la conexión (HTTP ${status})`
            : 'No se pudo probar la conexión',
        });
        this.testingConnection = false;
      },
    });
  }
}
